import React from "react";
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from "react-router-dom";
import { ConfigProvider } from "antd";
import antdTheme from "./theme/antdTheme";
import { rememberPostLoginRedirect } from "./js/libs/postLoginRedirect";

import Dashboard from "./pages/Dashboard";
import GroupSummary from "./pages/GroupSummary";
import GroupDetail from "./pages/GroupDetail";
import Profile from "./pages/Profile";
import Login from "./js/components/login/Login.react";
import "./js/components/login/login.css";

// Group Management
import GroupManagement from "./pages/GroupManagement";
import Onboard from "./pages/Onboard";
import Offboard from "./pages/Offboard";
import NmiChange from "./pages/NmiChange";

// Systems
import SystemStatus from "./pages/SystemStatus";
import SystemSearch from "./pages/SystemSearch";
import SystemDetail from "./pages/SystemDetail";

// Control Group
import ControlGroupSummary from "./pages/ControlGroupSummary";
import ControlGroupDetail from "./pages/ControlGroupDetail";
import BiddingSummary from "./pages/BiddingSummary";
import BiddingRules from "./pages/BiddingRules";

// Account Management
import AccountSummary from "./pages/AccountSummary";
import UserManagement from "./pages/UserManagement";
import RoleManagement from "./pages/RoleManagement";
import Statement from "./pages/Statement";

// Tickets
import TicketsSummary from "./pages/TicketsSummary";
import TicketsSearch from "./pages/TicketsSearch";
import TicketDetail from "./pages/TicketDetail";

// Dispatch
import DispatchSummary from "./pages/DispatchSummary";
import CurrentDispatch from "./pages/CurrentDispatch";
import CurrentDispatchDetail from "./pages/CurrentDispatchDetail";
import BookedDispatch from "./pages/BookedDispatch";
import DispatchReport from "./pages/DispatchReport";
import StrategyCentre from "./pages/StrategyCentre";

// Company
import CompanyList from "./pages/CompanyList";
import CompanySnapshot from "./pages/CompanySnapshot";
import CompanyAPI from "./pages/CompanyAPI";

// Customer Onboarding (Partner binding approval)
import PendingBindings from "./pages/PendingBindings";

// Settlements
import SettlementRuns from "./pages/SettlementRuns";
import SettlementRunDetail from "./pages/SettlementRunDetail";
import SettlementRuleManagement from "./pages/SettlementRuleManagement";
import OutstandingReceivable from "./pages/OutstandingReceivable";
import OutstandingPayable from "./pages/OutstandingPayable";
import HistoricalReceivable from "./pages/HistoricalReceivable";
import HistoricalPayments from "./pages/HistoricalPayments";
import SpecialJournals from "./pages/SpecialJournals";

// Configuration
import Equipment from "./pages/Equipment";
import VppPlans from "./pages/VppPlans";

// Audit Log
import AuditLog from "./pages/AuditLog";

// Bug Reports
import BugReportList from "./pages/BugReportList";
import BugReportDetail from "./pages/BugReportDetail";
import BugReportForm from "./pages/BugReportForm";

// Core Services
import EngineStatus from "./pages/EngineStatus";
import AemoSettlementConfig from "./pages/AemoSettlementConfig";
import RedisStatus from "./pages/RedisStatus";
import Backtest from "./pages/Backtest";

// Portal Guide
import PortalGuide from "./pages/PortalGuide";

// Engine Guide
import EngineGuide from "./pages/EngineGuide";

function isAuthenticated() {
  try {
    const token = localStorage.getItem('token');
    const userId = localStorage.getItem('userId');
    return Boolean(token || userId);
  } catch (_) {
    return false;
  }
}

function getDefaultRoute() {
  if (!isAuthenticated()) return "/login";
  const roleType = localStorage.getItem("roleType") || "";
  if (roleType === "SUPERADMIN") return "/dashboard";
  try {
    const perms = JSON.parse(localStorage.getItem("permissions") || "[]");
    if (perms.includes("dashboard:read")) return "/dashboard";
  } catch (_) {}
  return "/profile";
}

function ProtectedRoute() {
  const location = useLocation();
  if (isAuthenticated()) return <Outlet />;
  // Remember the deep link so Login can send them back to it after signing in.
  rememberPostLoginRedirect(location.pathname + location.search + location.hash);
  return <Navigate to="/login" replace />;
}

// Check if user has at least one of the required permissions (superadmin bypasses)
function hasPermission(requiredPerms) {
  const roleType = localStorage.getItem("roleType") || "";
  if (roleType === "SUPERADMIN") return true;
  if (!requiredPerms || requiredPerms.length === 0) return true;
  try {
    const perms = JSON.parse(localStorage.getItem("permissions") || "[]");
    return requiredPerms.some((p) => perms.includes(p));
  } catch (_) {
    return false;
  }
}

// Route guard: requires authentication + specific permissions
function PermissionRoute({ permissions, children }) {
  if (!isAuthenticated()) return <Navigate to="/login" replace />;
  if (!hasPermission(permissions)) return <Navigate to="/profile" replace />;
  return children;
}

function DefaultRedirect() {
  return <Navigate to={getDefaultRoute()} replace />;
}

export default function App() {
  return (
    <ConfigProvider theme={antdTheme}>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<DefaultRedirect />} />
          <Route path="/login" element={<Login />} />

          {/* All authenticated routes — ProtectedRoute checks token on every navigation */}
          <Route element={<ProtectedRoute />}>
            <Route path="/dashboard" element={<PermissionRoute permissions={["dashboard:read"]}><Dashboard /></PermissionRoute>} />
            <Route path="/dashboard/:userId" element={<PermissionRoute permissions={["dashboard:read"]}><Dashboard /></PermissionRoute>} />
            <Route path="/profile" element={<Profile />} />

            {/* Group Management */}
            <Route path="/group-management" element={<GroupManagement />} />
            <Route path="/group-management/groups" element={<GroupSummary />} />
            <Route path="/group-management/groups/:groupId" element={<GroupDetail />} />
            <Route path="/group-management/onboard" element={<Onboard />} />
            <Route path="/group-management/offboard" element={<Offboard />} />
            <Route path="/group-management/nmi-change" element={<PermissionRoute permissions={["approval:create", "approval:review"]}><NmiChange /></PermissionRoute>} />

            {/* Systems */}
            <Route path="/systems/summary" element={<SystemStatus />} />
            <Route path="/systems/search" element={<SystemSearch />} />
            <Route path="/systems/:systemSn" element={<SystemDetail />} />

            {/* Control Group — permissions mirror the VPP Control menu in
                PageLayout.react.js, so hiding a nav item also blocks its URL. */}
            <Route path="/control-group/summary" element={<PermissionRoute permissions={["dispatchgroup:read"]}><ControlGroupSummary /></PermissionRoute>} />
            <Route path="/control-group/:groupId" element={<PermissionRoute permissions={["dispatchgroup:read"]}><ControlGroupDetail /></PermissionRoute>} />

            {/* Bidding */}
            <Route path="/bidding/summary" element={<PermissionRoute permissions={["dispatch:read"]}><BiddingSummary /></PermissionRoute>} />
            <Route path="/bidding/rules" element={<PermissionRoute permissions={["dispatch:read"]}><BiddingRules /></PermissionRoute>} />

            {/* Account Management */}
            <Route path="/account/summary" element={<AccountSummary />} />
            <Route path="/account/users" element={<UserManagement />} />
            <Route path="/account/statement" element={<Statement />} />

            {/* Roles & Permissions (Superadmin only) */}
            <Route path="/roles" element={<RoleManagement />} />
            <Route path="/configuration/equipment" element={<Equipment />} />
            <Route path="/configuration/vpp-plans" element={<VppPlans />} />

            {/* Tickets */}
            <Route path="/tickets/summary" element={<TicketsSummary />} />
            <Route path="/tickets/search" element={<TicketsSearch />} />
            <Route path="/tickets/:ticketId" element={<TicketDetail />} />

            {/* Dispatch — /dispatch/report is gated on report:read to match its
                menu entry; the rest use dispatch:read. */}
            <Route path="/dispatch/summary" element={<PermissionRoute permissions={["dispatch:read"]}><DispatchSummary /></PermissionRoute>} />
            <Route path="/dispatch/current" element={<PermissionRoute permissions={["dispatch:read"]}><CurrentDispatch /></PermissionRoute>} />
            <Route path="/dispatch/current/:dispatchId" element={<PermissionRoute permissions={["dispatch:read"]}><CurrentDispatchDetail /></PermissionRoute>} />
            <Route path="/dispatch/booked" element={<PermissionRoute permissions={["dispatch:read"]}><BookedDispatch /></PermissionRoute>} />
            <Route path="/dispatch/report" element={<PermissionRoute permissions={["report:read"]}><DispatchReport /></PermissionRoute>} />
            <Route path="/dispatch/strategy" element={<PermissionRoute permissions={["dispatch:read"]}><StrategyCentre /></PermissionRoute>} />

            {/* Company */}
            <Route path="/customers/bindings" element={<PermissionRoute permissions={["binding:read"]}><PendingBindings /></PermissionRoute>} />
            <Route path="/company/list" element={<CompanyList />} />
            <Route path="/company/snapshot" element={<CompanySnapshot />} />
            <Route path="/company/snapshot/:companyId" element={<CompanySnapshot />} />

            {/* Settlements */}
            <Route path="/settlements/runs" element={<PermissionRoute permissions={["settlement:read"]}><SettlementRuns /></PermissionRoute>} />
            <Route path="/settlements/runs/:runId" element={<PermissionRoute permissions={["settlement:read"]}><SettlementRunDetail /></PermissionRoute>} />
            <Route path="/settlements/rules" element={<PermissionRoute permissions={["settlement:run"]}><SettlementRuleManagement /></PermissionRoute>} />
            <Route path="/settlements/journals" element={<PermissionRoute permissions={["settlement:read"]}><SpecialJournals /></PermissionRoute>} />
            <Route path="/settlements/receivable" element={<PermissionRoute permissions={["settlement:read"]}><OutstandingReceivable /></PermissionRoute>} />
            <Route path="/settlements/payable" element={<PermissionRoute permissions={["settlement:read"]}><OutstandingPayable /></PermissionRoute>} />
            <Route path="/settlements/history-receivable" element={<PermissionRoute permissions={["settlement:read"]}><HistoricalReceivable /></PermissionRoute>} />
            <Route path="/settlements/history-payments" element={<PermissionRoute permissions={["settlement:read"]}><HistoricalPayments /></PermissionRoute>} />

            {/* API */}
            <Route path="/api" element={<CompanyAPI />} />

            {/* Audit Log */}
            <Route path="/audit-log" element={<AuditLog />} />

            {/* Bug Reports */}
            <Route path="/bug-reports" element={<BugReportList />} />
            <Route path="/bug-reports/new" element={<BugReportForm />} />
            <Route path="/bug-reports/:id" element={<BugReportDetail />} />

            {/* Core Services */}
            <Route path="/core-services/engines" element={<EngineStatus />} />
            <Route path="/core-services/aemo-settlement" element={<AemoSettlementConfig />} />
            <Route path="/core-services/redis" element={<RedisStatus />} />
            <Route path="/core-services/backtest" element={<Backtest />} />
            <Route path="/core-services/backtest/:jobId" element={<Backtest />} />

            {/* Portal Guide — available to all authenticated users, content filtered by RBAC */}
            <Route path="/portal-guide" element={<PortalGuide />} />

            {/* Engine Guide — technical documentation for Realtime and Settlement engines */}
            <Route path="/engine-guide" element={<EngineGuide />} />
          </Route>

          <Route path="*" element={<DefaultRedirect />} />
        </Routes>
      </BrowserRouter>
    </ConfigProvider>
  );
}
